import { useCallback, useEffect, useState } from "react";

import { useDisplaySettingsRequest } from "./displaySettingsRequest";

/** How long the account menu gets to take focus before the Display submenu expands. */
const SUBMENU_OPEN_DELAY_MS = 80;

type DisplaySettingsMenu = {
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  displayOpen: boolean;
  onDisplayOpenChange: (open: boolean) => void;
};

/**
 * Open state for the account menu and its Display submenu, so the density
 * notice's "Display Settings" link can open the menu with Display expanded.
 * Wire `menuOpen` / `onMenuOpenChange` to the DropdownMenu and
 * `displayOpen` / `onDisplayOpenChange` to `DensityRow`.
 */
export function useDisplaySettingsMenu(
  onMenuOpened?: () => void
): DisplaySettingsMenu {
  const [menuOpen, setMenuOpen] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [displayRequested, setDisplayRequested] = useState(false);

  const onMenuOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      setDisplayOpen(false);
      setDisplayRequested(false);
    }
  }, []);

  const request = useCallback(() => {
    onMenuOpened?.();
    setDisplayRequested(true);
    setMenuOpen(true);
  }, [onMenuOpened]);
  useDisplaySettingsRequest(request);

  // The submenu closes itself if it mounts before the menu has taken focus,
  // so expand it once the menu has settled.
  useEffect(() => {
    if (!menuOpen || !displayRequested) return undefined;
    const timer = window.setTimeout(() => {
      setDisplayOpen(true);
      setDisplayRequested(false);
    }, SUBMENU_OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [menuOpen, displayRequested]);

  return {
    menuOpen,
    onMenuOpenChange,
    displayOpen,
    onDisplayOpenChange: setDisplayOpen
  };
}
